import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { showSystemErrorNotification } from "@/utils/notifications";

import {
  deleteCustomProcedureCode,
  loadCustomProcedureCodes,
  upsertCustomProcedureCode,
} from "../services/custom-codes";
import { sortProcedureCodes } from "../services/procedure-fees";
import { ProcedureCode } from "../types/fee-table";
import { CustomCodeFormType } from "../zod-schemas/custom-code";

export function useCustomCodes(refreshKey = 0) {
  const medplum = useMedplum();
  const { practiceId } = useParams<{ practiceId: string }>();
  const [procedures, setProcedures] = useState<ProcedureCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingCodes, setUpdatingCodes] = useState<Set<string>>(new Set());
  const proceduresRef = useRef<ProcedureCode[]>([]);

  type LoadCustomCodesOptions = { showLoading?: boolean };

  const loadCustomCodes = useCallback(
    async (options?: LoadCustomCodesOptions) => {
      const showLoading = options?.showLoading ?? true;

      if (!practiceId) {
        // Always clear: we are not loading; avoids a stuck spinner if a prior call left isLoading true.
        setIsLoading(false);
        return;
      }

      try {
        if (showLoading) {
          setIsLoading(true);
        }
        medplum.invalidateSearches("CodeSystem");
        medplum.invalidateSearches("ValueSet");
        medplum.invalidateSearches("QuestionnaireResponse");

        const procedureCodes = await loadCustomProcedureCodes(medplum, practiceId);
        const sortedProcedures = sortProcedureCodes(procedureCodes);
        proceduresRef.current = sortedProcedures;
        setProcedures(sortedProcedures);
      } catch (error) {
        showSystemErrorNotification();
        setProcedures([]);
        throw new Error("Error loading custom procedure codes: " + error);
      } finally {
        if (showLoading) {
          setIsLoading(false);
        }
      }
    },
    [medplum, practiceId],
  );

  useEffect(() => {
    loadCustomCodes();
  }, [loadCustomCodes, refreshKey]);

  const saveCustomCode = useCallback(
    async (formValues: CustomCodeFormType, existingCode?: string) => {
      if (!practiceId) {
        showSystemErrorNotification();
        throw new Error("Practice ID is required");
      }

      try {
        await upsertCustomProcedureCode(medplum, practiceId, formValues, existingCode);
        await loadCustomCodes({ showLoading: false });
      } catch (error) {
        showSystemErrorNotification();
        console.error("Error saving custom procedure code:", error);
        throw error;
      }
    },
    [medplum, practiceId, loadCustomCodes],
  );

  const removeCustomCode = useCallback(
    async (code: string) => {
      if (!practiceId) {
        showSystemErrorNotification();
        throw new Error("Practice ID is required");
      }

      try {
        await deleteCustomProcedureCode(medplum, practiceId, code);
        await loadCustomCodes({ showLoading: false });
      } catch (error) {
        showSystemErrorNotification();
        console.error("Error deleting custom procedure code:", error);
        throw error;
      }
    },
    [medplum, practiceId, loadCustomCodes],
  );

  const toggleStatus = useCallback(
    async (code: string, isActive: boolean) => {
      if (!practiceId) {
        return;
      }

      // Use functional update to check and set updating state atomically
      let shouldProceed = false;
      setUpdatingCodes((prev) => {
        // Prevent multiple clicks while update is in progress
        if (prev.has(code)) {
          return prev;
        }
        shouldProceed = true;
        const next = new Set(prev);
        next.add(code);
        return next;
      });

      if (!shouldProceed) {
        return;
      }

      // Get current procedure from ref
      const procedure = proceduresRef.current.find((p) => p.code === code);
      if (!procedure) {
        // Remove from updating set if procedure not found
        setUpdatingCodes((prev) => {
          const next = new Set(prev);
          next.delete(code);
          return next;
        });
        return;
      }

      const originalIsActive = procedure.isActive;

      // Optimistic update
      setProcedures((prev) => {
        const updated = prev.map((p) => (p.code === code ? { ...p, isActive } : p));
        proceduresRef.current = updated;
        return updated;
      });

      try {
        await upsertCustomProcedureCode(
          medplum,
          practiceId,
          {
            code: procedure.code,
            name: procedure.name,
            information: procedure.information,
            insuranceFee: procedure.insuranceFee ?? null,
            selfPayFee: procedure.selfPayFee ?? null,
            isActive,
          },
          procedure.code,
        );
        await loadCustomCodes({ showLoading: false });
      } catch (error) {
        // Revert on error
        setProcedures((prev) => {
          const reverted = prev.map((p) =>
            p.code === code ? { ...p, isActive: originalIsActive } : p,
          );
          proceduresRef.current = reverted;
          return reverted;
        });
        showSystemErrorNotification();
        throw new Error("Error toggling custom code status: " + error);
      } finally {
        // Remove from updating set
        setUpdatingCodes((prev) => {
          const next = new Set(prev);
          next.delete(code);
          return next;
        });
      }
    },
    [medplum, practiceId, loadCustomCodes],
  );

  const isUpdating = useCallback(
    (code: string) => {
      return updatingCodes.has(code);
    },
    [updatingCodes],
  );

  return {
    procedures,
    isLoading,
    saveCustomCode,
    removeCustomCode,
    toggleStatus,
    isUpdating,
  };
}
